import * as THREE from 'three';
import { BASE_COLS, BASE_ROWS, CELL_SIZE, FIELD, PALETTE } from '../game/config';
import { cellToWorld } from '../game/NavigationSystem';
import type { BaseObjects, EnemyType, HealthBar, Obstacle, ObstacleType, TankGroup } from '../game/types';
import { MAPS } from './assets';
import { makeMaterial } from './materials';

type BlockSpec = { col: number; row: number; x: number; z: number; w: number; d: number; type: ObstacleType };

const OBSTACLE_LAYOUT: Array<readonly [number, number, ObstacleType]> = [
  [1, 2, 'rock'], [2, 2, 'brick'], [5, 2, 'brick'], [6, 2, 'brick'], [10, 2, 'brick'], [11, 2, 'rock'],
  [0, 4, 'brick'], [1, 4, 'brick'], [4, 4, 'rock'], [8, 4, 'brick'], [9, 4, 'brick'], [12, 4, 'brick'],
  [2, 6, 'brick'], [3, 6, 'brick'], [6, 6, 'brick'], [10, 6, 'brick'], [11, 6, 'rock'],
  [0, 8, 'brick'], [1, 8, 'brick'], [4, 8, 'brick'], [8, 8, 'brick'], [9, 8, 'brick'], [12, 8, 'rock'],
  [2, 10, 'brick'], [3, 10, 'brick'], [6, 10, 'brick'], [10, 10, 'brick'], [11, 10, 'brick'],
  [1, 11, 'rock'], [4, 11, 'brick'], [9, 11, 'brick'], [12, 11, 'brick'],
];

export function createFieldFrame(scene: THREE.Scene): void {
  const frameMaterial = new THREE.MeshBasicMaterial({ color: 0x367780, transparent: true, opacity: 0.38 });
  const beamGeometry = new THREE.BoxGeometry(0.055, 0.13, FIELD.depth);
  [-FIELD.width / 2, FIELD.width / 2].forEach((x) => {
    const beam = new THREE.Mesh(beamGeometry, frameMaterial);
    beam.position.set(x, 0.08, 0);
    scene.add(beam);
  });
  const endGeometry = new THREE.BoxGeometry(FIELD.width, 0.13, 0.055);
  [FIELD.top, FIELD.bottom].forEach((z) => {
    const beam = new THREE.Mesh(endGeometry, frameMaterial);
    beam.position.set(0, 0.08, z);
    scene.add(beam);
  });
  const markerMaterial = new THREE.MeshBasicMaterial({ color: PALETTE.orange, transparent: true, opacity: 0.5 });
  for (let x = FIELD.left + 1; x <= FIELD.right - 1; x += 2) {
    const marker = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.04, 0.5), markerMaterial);
    marker.position.set(x, 0.07, FIELD.baseZ - BASE_ROWS * CELL_SIZE / 2 - 0.28);
    scene.add(marker);
  }
}

export function createObstacles(scene: THREE.Scene): Obstacle[] {
  return OBSTACLE_LAYOUT.map(([col, row, type], index) => {
    const point = cellToWorld(col, row);
    const spec: BlockSpec = { col, row, x: point.x, z: point.z, w: CELL_SIZE, d: CELL_SIZE, type };
    const mesh = createBlock(spec, index);
    mesh.position.set(spec.x, 0, spec.z);
    scene.add(mesh);
    const destructible = type === 'brick';
    return {
      mesh, x: spec.x, z: spec.z, col, row,
      halfWidth: CELL_SIZE / 2, halfDepth: CELL_SIZE / 2,
      type, destructible, hp: destructible ? 4 : Infinity, maxHp: destructible ? 4 : Infinity,
    };
  });
}

function createBlock(spec: BlockSpec, index: number): THREE.Group {
  const group = new THREE.Group();
  const isRock = spec.type === 'rock';
  const height = 0.98;
  const texture = MAPS.brick;
  if (isRock) {
    const wall = new THREE.Mesh(new THREE.BoxGeometry(spec.w, height, spec.d), new THREE.MeshStandardMaterial({ map: texture, color: 0x727b7e, emissive: 0x242b2e, emissiveMap: texture, emissiveIntensity: 0.18, roughness: 0.94, metalness: 0.08 }));
    wall.position.y = height / 2;
    wall.castShadow = true;
    wall.receiveShadow = true;
    group.add(wall);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(spec.w * 0.88, 0.075, spec.d * 0.88), new THREE.MeshStandardMaterial({ color: 0x9ba5a7, emissive: 0x303638, emissiveIntensity: 0.12, roughness: 0.9, metalness: 0.12 }));
    cap.position.y = height + 0.0375;
    cap.castShadow = true;
    group.add(cap);
    const seamMaterial = new THREE.MeshBasicMaterial({ color: 0x4a5357, transparent: true, opacity: 0.82 });
    const horizontalSeam = new THREE.Mesh(new THREE.BoxGeometry(spec.w * 0.9, 0.025, 0.035), seamMaterial);
    horizontalSeam.position.set(0, height * 0.52, -spec.d / 2 - 0.018);
    group.add(horizontalSeam);
    const verticalSeam = new THREE.Mesh(new THREE.BoxGeometry(0.035, height * 0.48, 0.035), seamMaterial);
    verticalSeam.position.set((index % 2 ? 0.22 : -0.22), height * 0.25, -spec.d / 2 - 0.019);
    group.add(verticalSeam);
  } else {
    const body = new THREE.Mesh(new THREE.BoxGeometry(spec.w, height, spec.d), new THREE.MeshStandardMaterial({ map: texture, color: 0xb06e4d, emissive: 0x351b14, emissiveMap: texture, emissiveIntensity: 0.12, roughness: 0.96, metalness: 0.04 }));
    body.position.y = height / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(spec.w * 0.76, 0.045, 0.08), new THREE.MeshBasicMaterial({ color: index % 2 ? PALETTE.cyan : PALETTE.orange, transparent: true, opacity: 0.58 }));
    cap.position.set(0, height + 0.025, -spec.d / 2 - 0.005);
    group.add(cap);
    for (let x = -spec.w / 2 + 0.35; x < spec.w / 2; x += 0.7) {
      const bolt = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 6), new THREE.MeshBasicMaterial({ color: 0x8e9b93 }));
      bolt.position.set(x, height * 0.7, -spec.d / 2 - 0.025);
      group.add(bolt);
    }
  }
  return group;
}

export function createTank(isPlayer = false, type: EnemyType = 'scout'): TankGroup {
  const group = new THREE.Group() as TankGroup;
  const launcher = !isPlayer && type === 'launcher';
  const bulldozer = !isPlayer && type === 'bulldozer';
  const bodyColor = isPlayer ? 0x3e8248 : type === 'heavy' ? 0x263744 : launcher ? 0x49375d : bulldozer ? 0x79512f : 0x344958;
  const accentColor = isPlayer ? PALETTE.green : type === 'heavy' ? 0x6c9ab7 : launcher ? 0xb889f2 : bulldozer ? 0xe0a34f : 0x4d7f9d;
  const body = new THREE.Mesh(new THREE.BoxGeometry(isPlayer ? 1.65 : bulldozer ? 1.72 : launcher ? 1.6 : 1.45, isPlayer ? 0.45 : 0.4, isPlayer ? 1.28 : bulldozer ? 1.22 : 1.12), makeMaterial(bodyColor, isPlayer ? PALETTE.greenDark : 0x1c2c3b, 0.45));
  body.position.y = 0.43;
  body.castShadow = true;
  group.add(body);
  const front = new THREE.Mesh(new THREE.BoxGeometry(isPlayer ? 1.1 : 0.9, 0.2, 0.25), makeMaterial(accentColor, accentColor, 0.3));
  front.position.set(0, 0.67, isPlayer ? -0.56 : 0.48);
  group.add(front);
  [-0.62, 0.62].forEach((x) => {
    const tread = new THREE.Mesh(new THREE.BoxGeometry(0.25, 0.25, isPlayer ? 1.43 : 1.25), makeMaterial(0x172128, 0x10161b, 0.1));
    tread.position.set(x, 0.27, 0);
    tread.castShadow = true;
    group.add(tread);
    [-0.42, 0, 0.42].forEach((z) => {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.27, 12), makeMaterial(0x34444a, 0x10171d, 0.12));
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(x, 0.27, z);
      group.add(wheel);
    });
  });
  if (bulldozer) {
    const blade = new THREE.Mesh(new THREE.BoxGeometry(1.92, 0.43, 0.16), makeMaterial(0xb2753d, 0x432719, 0.3));
    blade.position.set(0, 0.45, 0.79);
    blade.rotation.x = -0.12;
    blade.castShadow = true;
    group.add(blade);
    [-0.62, 0.62].forEach((x) => {
      const strut = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.55, 0.1), makeMaterial(0x4b3526, 0x241a15, 0.2));
      strut.position.set(x, 0.47, 0.66);
      strut.rotation.x = -0.2;
      group.add(strut);
    });
  }
  const weaponMount = new THREE.Group();
  if (launcher) {
    const platform = new THREE.Mesh(new THREE.BoxGeometry(1.18, 0.18, 0.78), makeMaterial(0x342940, 0x1b1524, 0.42));
    platform.position.y = 0.74;
    weaponMount.add(platform);
    [-0.3, 0.3].forEach((x) => {
      const pod = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.25, 0.88), makeMaterial(accentColor, 0x35214d, 0.5));
      pod.position.set(x, 0.94, 0.2);
      pod.rotation.x = -0.13;
      pod.castShadow = true;
      weaponMount.add(pod);
    });
  } else {
    const turret = new THREE.Mesh(new THREE.CylinderGeometry(isPlayer ? 0.42 : 0.34, isPlayer ? 0.47 : 0.38, 0.23, 12), makeMaterial(isPlayer ? 0x5a9a54 : bulldozer ? 0x6e482e : 0x3a5262, isPlayer ? PALETTE.greenDark : 0x21384b, 0.5));
    turret.position.y = 0.76;
    weaponMount.add(turret);
    const cannon = new THREE.Mesh(new THREE.BoxGeometry(isPlayer ? 0.19 : 0.15, 0.18, isPlayer ? 0.95 : 0.78), makeMaterial(accentColor, accentColor, 0.5));
    cannon.position.set(0, 0.8, isPlayer ? -0.58 : 0.53);
    cannon.castShadow = true;
    weaponMount.add(cannon);
  }
  const light = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.11, 0.11), new THREE.MeshBasicMaterial({ color: accentColor }));
  light.position.set(0, 1.02, 0);
  weaponMount.add(light);
  group.add(weaponMount);
  group.userData = { weaponMount };
  const healthBar = createHealthBar(isPlayer ? 1.35 : 1.1, isPlayer ? PALETTE.green : PALETTE.red);
  healthBar.group.position.y = isPlayer ? 1.5 : 1.32;
  group.userData.healthBar = healthBar;
  group.add(healthBar.group);
  if (isPlayer) {
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.9, 0.97, 32), new THREE.MeshBasicMaterial({ color: PALETTE.green, transparent: true, opacity: 0.38, side: THREE.DoubleSide }));
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.06;
    group.add(ring);
  }
  return group;
}

export function createBase(): BaseObjects {
  const group = new THREE.Group();
  const pad = new THREE.Mesh(new THREE.BoxGeometry(BASE_COLS * CELL_SIZE - 0.12, 0.25, BASE_ROWS * CELL_SIZE - 0.12), makeMaterial(0x1d3037, 0x17343a, 0.35));
  pad.position.y = 0.14;
  pad.castShadow = true;
  group.add(pad);
  const wall = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.58, 2.9), makeMaterial(0x263d42, 0x1f4248, 0.4));
  wall.position.y = 0.5;
  wall.castShadow = true;
  group.add(wall);
  const core = new THREE.Mesh(new THREE.BoxGeometry(1.36, 0.82, 1.36), makeMaterial(0x7d463a, 0x6d231f, 0.6));
  core.position.y = 1.08;
  core.castShadow = true;
  group.add(core);
  const roof = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.18, 1.7), makeMaterial(PALETTE.orange, 0xaa4829, 0.75));
  roof.position.y = 1.57;
  group.add(roof);
  const shieldShape = new THREE.Shape();
  shieldShape.moveTo(-2.02, -2.02);
  shieldShape.lineTo(2.02, -2.02);
  shieldShape.lineTo(2.02, 2.02);
  shieldShape.lineTo(-2.02, 2.02);
  shieldShape.closePath();
  const shieldHole = new THREE.Path();
  shieldHole.moveTo(-1.94, -1.94);
  shieldHole.lineTo(-1.94, 1.94);
  shieldHole.lineTo(1.94, 1.94);
  shieldHole.lineTo(1.94, -1.94);
  shieldHole.closePath();
  shieldShape.holes.push(shieldHole);
  const shield = new THREE.Mesh(new THREE.ShapeGeometry(shieldShape), new THREE.MeshBasicMaterial({ color: PALETTE.orange, transparent: true, opacity: 0.7, side: THREE.DoubleSide }));
  shield.rotation.x = -Math.PI / 2;
  shield.position.y = 0.08;
  group.add(shield);
  for (let i = 0; i < 4; i++) {
    const pylon = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.58, 0.16), makeMaterial(PALETTE.cyan, PALETTE.cyan, 0.55));
    const angle = i * Math.PI / 2 + Math.PI / 4;
    pylon.position.set(Math.cos(angle) * 1.4, 0.7, Math.sin(angle) * 1.4);
    group.add(pylon);
  }
  const baseLight = new THREE.PointLight(PALETTE.orange, 10, 8);
  baseLight.position.y = 2;
  group.add(baseLight);
  const healthBar = createHealthBar(1.8, PALETTE.orange);
  healthBar.group.position.y = 3.05;
  group.add(healthBar.group);
  return { group, core, shield, healthBar };
}

function createHealthBar(width: number, color: number): HealthBar {
  const group = new THREE.Group();
  const background = new THREE.Sprite(new THREE.SpriteMaterial({ color: 0x111a20, transparent: true, opacity: 0.92, depthTest: false, depthWrite: false }));
  background.scale.set(width, 0.14, 1);
  const fill = new THREE.Sprite(new THREE.SpriteMaterial({ color, transparent: true, opacity: 1, depthTest: false, depthWrite: false }));
  fill.scale.set(width - 0.04, 0.09, 1);
  fill.position.x = -0.02;
  group.add(background, fill);
  group.renderOrder = 20;
  return { group, fill, width: width - 0.04 };
}
